import { useState } from "react";
import { TIER_COLORS, TIER_ORDER, DAYS, COURTS, TIME_START_OPTIONS, courtTypeOf } from "../utils/tiers";
import { getGroupSchedule, getEndTime, addHours, durationOptionsFor } from "../utils/time";
import { hasAvailabilityConflict, getScheduleConflicts, groupsTimeOverlap } from "../utils/validation";
import { coachAvailableFor, hasAvailabilityConstraints } from "../utils/coachAvailability";
import { getWaitlist, isWaiting } from "../utils/waitlist";
import { otherClinicsForPlayer } from "../utils/membership";
import { PendingRequestList } from "./PendingChanges";

export default function GroupModal({ group, groupPlayers, allPlayers, allGroups = [], coaches = [], onClose, onPlayerClick, onAddPlayer, onRemovePlayer, onDeleteGroup, onUpdateGroup, onUpdatePlayerTier, onWaitlistAdd, onWaitlistRemove, onWaitlistMove, pendingRequests = [], currentUser, onApproveRequest, onRejectRequest, onCancelRequest }) {
  const [search, setSearch] = useState("");
  const [editingTier, setEditingTier] = useState(false);

  // Members whose own level no longer matches the group's — only meaningful
  // for instruction groups; weekend sessions mix levels by design.
  const mismatchedMembers = !group.kind
    ? groupPlayers.filter(p => p.tier !== group.tier)
    : [];

  // Waiting members resolved to player records, in queue order.
  const waitingPlayers = getWaitlist(group)
    .map(id => allPlayers.find(p => p.id === id))
    .filter(Boolean);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState(group.notes || "");
  const [editingSchedule, setEditingSchedule] = useState(false);
  const [scheduleDraft, setScheduleDraft] = useState(null);
  const colors = TIER_COLORS[group.tier] || TIER_COLORS["No Group"];
  const schedule = getGroupSchedule(group);

  // Clashes for the currently-saved schedule (drives the header badge) and,
  // while editing, for the in-progress draft (drives the live warning under
  // the pickers so you see the conflict before committing it).
  const savedConflicts = getScheduleConflicts(group, allGroups);
  const draftConflicts = scheduleDraft
    ? getScheduleConflicts({ id: group.id, ...scheduleDraft }, allGroups)
    : [];

  function describeConflict(g) {
    const s = getGroupSchedule(g);
    return `${g.id} (${g.day} · ${s.startTime}–${addHours(s.startTime, s.duration)} · ${g.court})`;
  }

  const assignedCoach = coaches.find(c => c.id === group.coachId);
  // Other groups this same coach is booked for at an overlapping time —
  // a coach can't be in two places at once, even on different courts.
  const coachClashes = assignedCoach
    ? allGroups.filter(g => g.id !== group.id && g.coachId === assignedCoach.id && groupsTimeOverlap(group, g))
    : [];
  // Whether the assigned coach's availability window covers this group's slot.
  const coachUnavailable = assignedCoach &&
    hasAvailabilityConstraints(assignedCoach) &&
    schedule.startTime && schedule.duration && group.day &&
    !coachAvailableFor(assignedCoach, group.day, schedule.startTime, schedule.duration);

  const memberIds = new Set(group.playerIds);
  const candidates = allPlayers.filter(p => {
    if (memberIds.has(p.id)) return false;
    if (!search.trim()) return false;
    return p.name.toLowerCase().includes(search.toLowerCase());
  });

  function handleAdd(player) {
    onAddPlayer(player);
    setSearch("");
  }

  function saveNotes() {
    onUpdateGroup(group.id, { notes: notesDraft });
    setEditingNotes(false);
  }

  function cancelNotes() {
    setNotesDraft(group.notes || "");
    setEditingNotes(false);
  }

  function startEditingSchedule() {
    setScheduleDraft({
      day: group.day || "",
      court: group.court || "",
      startTime: schedule.startTime || "",
      duration: schedule.duration || 1,
    });
    setEditingSchedule(true);
  }

  function cancelSchedule() {
    setEditingSchedule(false);
    setScheduleDraft(null);
  }

  function saveSchedule() {
    const courtType = courtTypeOf(scheduleDraft.court);
    onUpdateGroup(group.id, {
      day: scheduleDraft.day,
      court: scheduleDraft.court,
      courtType,
      startTime: scheduleDraft.startTime,
      duration: courtType === "Pickleball" ? 1 : scheduleDraft.duration,
      time: undefined,
    });
    setEditingSchedule(false);
    setScheduleDraft(null);
  }

  function handleScheduleCourtChange(newCourt) {
    setScheduleDraft(prev => {
      const options = durationOptionsFor(prev.startTime, courtTypeOf(newCourt));
      return { ...prev, court: newCourt, duration: options.includes(prev.duration) ? prev.duration : options[0] };
    });
  }

  function handleScheduleStartChange(newStart) {
    setScheduleDraft(prev => {
      const options = durationOptionsFor(newStart, courtTypeOf(prev.court));
      return { ...prev, startTime: newStart, duration: options.includes(prev.duration) ? prev.duration : options[0] };
    });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 max-h-[90vh] flex flex-col overflow-hidden"
        style={{ maxHeight: "90vh" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`${colors.header} ${colors.text} px-4 py-3 flex-shrink-0 flex flex-wrap items-start justify-between gap-2`}>
          <div className="min-w-0">
            <div className="font-bold text-base font-mono flex items-center gap-1.5">
              {group.id}
              {group.notes && <span title="This group has notes">📝</span>}
            </div>
            {editingTier ? (
              <select
                autoFocus
                value={group.tier}
                onChange={e => {
                  onUpdateGroup?.(group.id, { tier: e.target.value });
                  setEditingTier(false);
                }}
                onBlur={() => setEditingTier(false)}
                className="text-sm font-semibold text-gray-800 bg-white border border-gray-300 rounded px-1.5 py-0.5 my-0.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                {TIER_ORDER.map(t => <option key={t} value={t}>{t}</option>)}
                {!TIER_ORDER.includes(group.tier) && (
                  <option value={group.tier}>{group.tier}</option>
                )}
              </select>
            ) : (
              <button
                onClick={() => setEditingTier(true)}
                title="Change this group's level"
                className="text-sm font-semibold hover:underline decoration-dotted underline-offset-2 text-left"
              >
                {group.tier} <span className="opacity-60 text-xs">✎</span>
              </button>
            )}
            {group.day && schedule.startTime ? (
              <>
                <div className="text-xs opacity-75 mt-0.5">{group.day} · {schedule.startTime}–{getEndTime(group)}</div>
                <div className="text-xs opacity-75">{group.court}</div>
              </>
            ) : (
              <div className="text-xs opacity-75 mt-0.5 italic">Unscheduled</div>
            )}
            {savedConflicts.length > 0 && (
              <div
                className="text-xs mt-1 inline-flex items-center gap-1 bg-red-600 text-white rounded px-1.5 py-0.5 font-semibold"
                title={`Overlaps ${savedConflicts.map(describeConflict).join(", ")}`}
              >
                ⚠ Double-booked with {savedConflicts.map(g => g.id).join(", ")}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-shrink-0">
            {onDeleteGroup && (
              confirmDelete ? (
                <div className="flex flex-wrap items-center justify-end gap-1.5">
                  <span className="text-xs opacity-80">Delete group?</span>
                  <button
                    onClick={() => { onDeleteGroup(group.id); onClose(); }}
                    className="text-xs bg-red-600 hover:bg-red-700 text-white font-semibold px-2 py-0.5 rounded transition-colors"
                  >
                    Yes, delete
                  </button>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="text-xs opacity-70 hover:opacity-100 border border-current rounded px-2 py-0.5 transition-opacity"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmDelete(true)}
                  title="Delete group"
                  className="text-xs opacity-60 hover:opacity-100 hover:text-red-200 transition-all border border-current rounded px-2 py-0.5"
                >
                  Delete
                </button>
              )
            )}
            <button
              onClick={onClose}
              className="text-xl leading-none opacity-60 hover:opacity-100 transition-opacity"
            >
              ×
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-4 space-y-4">
          {/* Changes asked for on this group, above the roster: they're about
              to change what the roster says. None of them is in effect yet. */}
          <PendingRequestList
            requests={pendingRequests}
            currentUser={currentUser}
            players={allPlayers}
            groups={allGroups}
            onApprove={onApproveRequest}
            onReject={onRejectRequest}
            onCancel={onCancelRequest}
          />

          {/* Current players */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Players</span>
              <span className={`text-xs px-2 py-0.5 rounded font-semibold ${groupPlayers.length >= 4 ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                {groupPlayers.length}/4
              </span>
            </div>
            {groupPlayers.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No players assigned yet.</p>
            ) : (
              <ul className="space-y-1.5">
                {groupPlayers.map(p => (
                  <li
                    key={p.id}
                    className={`${colors.bg} ${colors.border} border rounded-lg px-3 py-2 flex items-center gap-2 group/player`}
                  >
                    <div
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => { onClose(); onPlayerClick(p); }}
                    >
                      <div className="font-medium text-gray-800 text-sm flex items-center gap-1">
                        {p.name}
                        {hasAvailabilityConflict(p, group) && (
                          <span className="text-amber-500 text-xs" title="Not available at this time slot">⚠</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 flex gap-2 flex-wrap">
                        {p.age != null && <span>Age {p.age}</span>}
                        <span>{p.skillLevel}</span>
                        {/* Removing them here only takes them out of this
                            clinic, so say which others they'd still be in. */}
                        {otherClinicsForPlayer(p.id, allGroups, group.id).length > 0 && (
                          <span className="text-blue-700" title="Also enrolled in another clinic">
                            · also in {otherClinicsForPlayer(p.id, allGroups, group.id).map(g => g.id).join(", ")}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => onRemovePlayer && onRemovePlayer(p)}
                      title="Remove from group"
                      className="flex-shrink-0 text-gray-300 hover:text-red-500 transition-colors text-lg leading-none opacity-0 group-hover/player:opacity-100"
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Members keep their own level when a group's changes — dropping a
              group to a lower tier doesn't mean everyone in it was reassessed.
              Offered rather than applied, since only a coach knows which. */}
          {mismatchedMembers.length > 0 && (
            <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex flex-wrap items-center gap-2">
              <span className="text-amber-800 flex-1 min-w-0">
                {mismatchedMembers.length} member{mismatchedMembers.length !== 1 ? "s are" : " is"} still
                listed at a different level to this group.
              </span>
              <button
                onClick={() => mismatchedMembers.forEach(p => onUpdatePlayerTier?.(p.id, group.tier))}
                className="text-amber-900 font-semibold hover:underline whitespace-nowrap"
              >
                Set {mismatchedMembers.length === 1 ? "them" : "them all"} to {group.tier}
              </button>
            </div>
          )}

          {/* Waitlist — people who want in. Kept apart from Players: nobody
              here is in the group, and their own groupId is untouched, so a
              member can be placed elsewhere and still be waiting for this. */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Waitlist</span>
              {waitingPlayers.length > 0 && (
                <span className="text-xs px-2 py-0.5 rounded font-semibold bg-slate-200 text-slate-700">
                  {waitingPlayers.length} waiting
                </span>
              )}
            </div>
            {waitingPlayers.length === 0 ? (
              <p className="text-sm text-gray-400 italic">Nobody waiting. Search below to add someone.</p>
            ) : (
              <ul className="space-y-1.5">
                {waitingPlayers.map((p, i) => (
                  <li
                    key={p.id}
                    // Dashed and neutral so a queue never looks like a tier's
                    // player rows — purple is Junior Mini's colour.
                    className="bg-slate-50 border border-dashed border-slate-300 rounded-lg px-3 py-2 flex items-center gap-2 group/wait"
                  >
                    <span className="text-xs font-semibold text-slate-500 w-4 flex-shrink-0">{i + 1}</span>
                    <div
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => { onClose(); onPlayerClick(p); }}
                    >
                      <div className="font-medium text-gray-800 text-sm flex items-center gap-1">
                        {p.name}
                        {hasAvailabilityConflict(p, group) && (
                          <span className="text-amber-500 text-xs" title="Not available at this time slot">⚠</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 flex gap-2 flex-wrap">
                        {p.age != null && <span>Age {p.age}</span>}
                        <span>{p.tier}</span>
                        {p.groupId && <span>· in {p.groupId}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <button
                        onClick={() => onWaitlistMove?.(group.id, p.id, -1)}
                        disabled={i === 0}
                        title="Move up the queue"
                        className="text-gray-300 hover:text-slate-700 disabled:opacity-0 transition-colors text-xs px-0.5"
                      >
                        ▲
                      </button>
                      <button
                        onClick={() => onWaitlistMove?.(group.id, p.id, 1)}
                        disabled={i === waitingPlayers.length - 1}
                        title="Move down the queue"
                        className="text-gray-300 hover:text-slate-700 disabled:opacity-0 transition-colors text-xs px-0.5"
                      >
                        ▼
                      </button>
                      <button
                        onClick={() => onAddPlayer && onAddPlayer(p)}
                        title="Place them in this group now"
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium px-1.5 transition-colors"
                      >
                        Place
                      </button>
                      <button
                        onClick={() => onWaitlistRemove?.(group.id, p.id)}
                        title="Take off the waitlist"
                        className="text-gray-300 hover:text-red-500 transition-colors text-lg leading-none"
                      >
                        ×
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Schedule */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Schedule</span>
              {!editingSchedule && (
                <button
                  onClick={startEditingSchedule}
                  className="text-xs font-medium text-gray-500 hover:text-gray-800 transition-colors"
                >
                  Edit
                </button>
              )}
            </div>
            {editingSchedule ? (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={scheduleDraft.day}
                    onChange={e => setScheduleDraft(prev => ({ ...prev, day: e.target.value }))}
                    className="border border-gray-300 rounded px-2 py-1.5 text-xs w-full focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                  >
                    <option value="">— Unscheduled —</option>
                    {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <select
                    value={scheduleDraft.court}
                    onChange={e => handleScheduleCourtChange(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1.5 text-xs w-full focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                  >
                    <option value="">— No court —</option>
                    {COURTS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={scheduleDraft.startTime}
                    onChange={e => handleScheduleStartChange(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1.5 text-xs w-full focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                  >
                    <option value="">— Start time —</option>
                    {TIME_START_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <select
                    value={scheduleDraft.duration}
                    onChange={e => setScheduleDraft(prev => ({ ...prev, duration: Number(e.target.value) }))}
                    disabled={courtTypeOf(scheduleDraft.court) === "Pickleball"}
                    className={`border border-gray-300 rounded px-2 py-1.5 text-xs w-full focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white ${
                      courtTypeOf(scheduleDraft.court) === "Pickleball" ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    {durationOptionsFor(scheduleDraft.startTime, courtTypeOf(scheduleDraft.court)).map(h => (
                      <option key={h} value={h}>{h} hour{h !== 1 ? "s" : ""}</option>
                    ))}
                  </select>
                </div>
                {courtTypeOf(scheduleDraft.court) === "Pickleball" && (
                  <p className="text-xs text-gray-500">Pickleball sessions are fixed at 1 hour.</p>
                )}
                {scheduleDraft.startTime && (
                  <p className="text-xs text-gray-500">Ends at {addHours(scheduleDraft.startTime, scheduleDraft.duration)}.</p>
                )}
                {draftConflicts.length > 0 && (
                  <div className="text-xs bg-red-50 border border-red-200 text-red-700 rounded px-2 py-1.5">
                    <span className="font-semibold">⚠ Overlaps another group on this court:</span>
                    <ul className="mt-0.5 list-disc list-inside">
                      {draftConflicts.map(g => (
                        <li key={g.id}>{describeConflict(g)}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={saveSchedule}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium py-1.5 rounded transition-colors"
                  >
                    Save
                  </button>
                  <button
                    onClick={cancelSchedule}
                    className="flex-1 border border-gray-300 hover:bg-gray-50 text-gray-600 text-xs font-medium py-1.5 rounded transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : group.day && schedule.startTime ? (
              <p className="text-sm text-gray-700">
                {group.day} · {schedule.startTime}–{getEndTime(group)} · {group.court}
              </p>
            ) : (
              <p className="text-sm text-gray-400 italic">Unscheduled</p>
            )}
          </div>

          {/* Coach */}
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Coach</div>
            <select
              value={group.coachId || ""}
              onChange={e => onUpdateGroup(group.id, { coachId: e.target.value || null })}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
            >
              <option value="">— Unassigned —</option>
              {coaches.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {coaches.length === 0 && (
              <p className="text-xs text-gray-400 mt-1">Add coaches under Members → Coaches to assign one here.</p>
            )}
            {coachClashes.length > 0 && (
              <div className="text-xs bg-red-50 border border-red-200 text-red-700 rounded px-2 py-1.5 mt-1.5">
                ⚠ {assignedCoach.name} is also booked at this time for {coachClashes.map(g => g.id).join(", ")}.
              </div>
            )}
            {coachUnavailable && (
              <div className="text-xs bg-amber-50 border border-amber-200 text-amber-700 rounded px-2 py-1.5 mt-1.5">
                ⚠ {assignedCoach.name} isn't available at this day/time per their set availability.
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Notes</span>
              {!editingNotes && (
                <button
                  onClick={() => setEditingNotes(true)}
                  className="text-xs font-medium text-gray-500 hover:text-gray-800 transition-colors"
                >
                  Edit
                </button>
              )}
            </div>
            {editingNotes ? (
              <div className="space-y-1.5">
                <textarea
                  value={notesDraft}
                  onChange={e => setNotesDraft(e.target.value)}
                  placeholder="Any notes about this group…"
                  rows={3}
                  autoFocus
                  className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                />
                <div className="flex gap-2">
                  <button
                    onClick={saveNotes}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium py-1.5 rounded transition-colors"
                  >
                    Save
                  </button>
                  <button
                    onClick={cancelNotes}
                    className="flex-1 border border-gray-300 hover:bg-gray-50 text-gray-600 text-xs font-medium py-1.5 rounded transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : group.notes ? (
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{group.notes}</p>
            ) : (
              <p className="text-sm text-gray-400 italic">None</p>
            )}
          </div>

          {/* Add member */}
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Add Member</div>
            <input
              type="text"
              placeholder="Search by name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            {search.trim() && (
              <div className="mt-1.5 space-y-1 max-h-40 overflow-y-auto">
                {candidates.length === 0 ? (
                  <p className="text-xs text-gray-400 italic px-1 py-1">No players found.</p>
                ) : (
                  candidates.map(p => (
                    <div
                      key={p.id}
                      className="border border-gray-200 rounded-lg px-3 py-2 text-xs flex items-center gap-2"
                    >
                      <span className="flex-1 min-w-0">
                        <span className="font-medium text-gray-800">{p.name}</span>
                        <span className="text-gray-400 ml-2">{p.tier}</span>
                        {p.age != null && <span className="text-gray-400 ml-2">· Age {p.age}</span>}
                        {p.groupId && <span className="text-gray-400 ml-2">· currently {p.groupId}</span>}
                      </span>
                      <button
                        onClick={() => handleAdd(p)}
                        className="text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap transition-colors"
                      >
                        Add
                      </button>
                      <button
                        onClick={() => { onWaitlistAdd?.(group.id, p.id); setSearch(""); }}
                        disabled={isWaiting(group, p.id)}
                        title={isWaiting(group, p.id) ? "Already on this waitlist" : "Put them on the waitlist instead of placing them"}
                        className="text-slate-600 hover:text-slate-900 disabled:text-gray-300 font-medium whitespace-nowrap transition-colors"
                      >
                        {isWaiting(group, p.id) ? "Waiting" : "Waitlist"}
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
